import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AppShell from '../layouts/AppShell';
import AdminLayout from '../layouts/AdminLayout';
import StatsCards from '../components/StatsCards';
import AnalyticsOverview from '../components/AnalyticsOverview';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { hasPermission, hasAnyPermission } from '../utils/permissionUtils';
import { buildNavigationItems } from '../utils/navigationConfig';

const StatsPage = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [periodDays, setPeriodDays] = useState(7);

  const canManageTemplates = useMemo(() => hasPermission(user, 'template:manage'), [user]);
  const canManageIntegrations = useMemo(() => hasPermission(user, 'integration:manage'), [user]);
  const canManagePositions = useMemo(() => hasPermission(user, 'position:manage'), [user]);
  const canManageTeams = useMemo(() => hasPermission(user, 'team:manage'), [user]);
  const canViewUserRoster = useMemo(
    () => hasAnyPermission(user, ['position:assign', 'position:manage', 'user:manage']),
    [user]
  );
  const canInviteUsers = useMemo(() => hasPermission(user, 'user:invite'), [user]);
  const canViewStats = useMemo(() => hasPermission(user, 'stats:view'), [user]);
  const canModerateComments = useMemo(() => hasPermission(user, 'comment:moderate'), [user]);

  const navItems = useMemo(
    () =>
      buildNavigationItems({
        canManageTemplates,
        canViewUserRoster,
        canManagePositions,
        canManageTeams,
        canInviteUsers,
        canViewStats,
        canManageIntegrations,
        canModerateComments
      }),
    [
      canManageTemplates,
      canViewUserRoster,
      canManagePositions,
      canManageTeams,
      canInviteUsers,
      canViewStats,
      canManageIntegrations,
      canModerateComments
    ]
  );

  const loadStats = useCallback(async () => {
    if (!canViewStats) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days: periodDays },
        timeout: 30000
      });
      setStats(response.data);
    } catch (fetchError) {
      console.error('Failed to load analytics:', fetchError);
      setError(fetchError.response?.data?.detail || fetchError.message || 'Unable to load analytics');
    } finally {
      setLoading(false);
    }
  }, [canViewStats, periodDays]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <AppShell user={user} navItems={navItems} onLogout={onLogout}>
      <AdminLayout
        title="Analytics"
        description="Monitor DM volume, load distribution, and agent availability."
        actions={
          canViewStats && (
            <div className="flex items-center gap-2">
              <div className="flex rounded-md border border-[var(--tg-border-soft)] overflow-hidden">
                {[7, 30, 90].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setPeriodDays(days)}
                    className={`px-2.5 py-1.5 text-xs transition-colors ${periodDays === days ? 'bg-purple-600 text-white' : 'text-[var(--tg-text-secondary)] hover:bg-[var(--tg-surface-raised)]'}`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
              <Button variant="outline" onClick={loadStats} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          )
        }
      >
        <div className="flex-1 min-h-0">
          {!canViewStats ? (
            <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-6 text-sm text-[var(--tg-text-secondary)]">
              You do not have permission to view analytics.
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-sm text-red-100">
              {error}
            </div>
          ) : loading && !stats ? (
            <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-6 flex justify-center">
              <div className="h-10 w-10 rounded-full border-t-2 border-b-2 border-purple-500 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              <StatsCards stats={stats} />
              <AnalyticsOverview stats={stats} />
              <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-4 text-sm text-[var(--tg-text-secondary)]">
                Analytics refresh from the latest chat activity. Use the workload cards to spot an
                uneven queue before reshuffling teams.
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    </AppShell>
  );
};

export default StatsPage;
